import { useState } from "react";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/common/Table/ItemsPerPage";
import { Link } from "react-router-dom";
import { getNestedValue } from "@/utils/helper/form";
import moment from "moment";
import TruncatedToolTipChildren from "@/common/ui/TruncatedToolTipChildren";

interface TableViewDropdownOption<T> {
  label: string;
  onClickFun: (data: T) => void;
  type?: string;
  show?: (row: T) => boolean;
}

interface CommonTableViewProps<T> {
  className?: string;
  data: T[];
  columnsConfig: ColumnDef<T>[];
  columnClassName?: string;
  rowSelection: any;
  setRowSelection: any;
  setSelectedRows: (rows: string[]) => void;
  openModal: (modalKey: string, props?: any) => void;
  paginationConfig?: {
    initialPage?: number;
    initialPerPage?: number;
    perPageOptions?: number[];
  };
  dropdownOptions: TableViewDropdownOption<T>[];
  linkColumns?: { id: string; url: string; identifier: string; isExternal?: boolean }[];
  hidePagination?: boolean;
}

const CommonTableView = <T extends { id: string }>({
  className = "",
  data,
  columnsConfig,
  dropdownOptions,
  rowSelection,
  setRowSelection,
  columnClassName = "max-w-[170px] w-[170px] min-w-[170px]",
  paginationConfig = { initialPage: 1, initialPerPage: 25, perPageOptions: [10, 25, 50, 100] },
  linkColumns,
  hidePagination = false,
}: CommonTableViewProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(paginationConfig.initialPage!);
  const [perPage, setPerPage] = useState({ value: paginationConfig.initialPerPage!, label: `Records per page ${paginationConfig.initialPerPage!}` });

  const table = useReactTable({
    data: data,
    columns: columnsConfig,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
  });

  return data.length > 0 ? (
    <div className={className}>
      <div className="h-full">
        <Table className="border-separate border-spacing-0 w-full">
          <TableHeader className="sticky top-0 z-10 bg-white whitespace-nowrap">
            <TableRow className="w-fit">
              <TableHead className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] border-b border-r border-t border-stroke-light" />
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={`!text-text-default font-bold px-3 border-b border-r border-t border-stroke-light ${
                      header.column.id === "select" ? "!w-[40px] !max-w-[40px] !min-w-[40px]" : columnClassName
                    }`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: any) => {
                return (
                  <TableRow key={row.id} className="group relative" data-state={row.getIsSelected() && "selected"}>
                    <TableCell className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] py-0.5 pl-1 border-b border-r border-stroke-light">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4 text-text-default" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {dropdownOptions
                            .filter((option) => (typeof option.show === "function" ? option.show(row.original) : true))
                            .map((option, index) => (
                              <DropdownMenuItem
                                key={index}
                                onClick={() => option.onClickFun(row.original)}
                                className={`cursor-pointer px-3 py-2 text-13 ${
                                  option.type === "remove"
                                    ? "text-text-error data-[highlighted]:text-text-error"
                                    : "text-text-default data-[highlighted]:text-text-primary"
                                } data-[highlighted]:bg-primaryOpacity-10 focus:bg-primaryOpacity-10 focus:text-text-primary`}
                              >
                                {option.label}
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    {row.getVisibleCells().map((cell: any) => {
                      if (cell.column.id === "select") return null;
                      const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                      const linkConfig = linkColumns?.find((link) => link.id === cell.column.id);
                      const identifierValue = linkConfig ? getNestedValue(row.original, linkConfig.identifier) : null;
                      // console.log({ column: cell.column.id, linkConfig, data: row.original });
                      return (
                        <TableCell
                          key={cell.id}
                          className={
                            cell.column.id === "select"
                              ? "!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] py-0.5 pl-1 border-b border-r border-stroke-light"
                              : `!text-text-default px-3 font-normal !leading-normal !text-13 border-b border-r border-stroke-light ${columnClassName}`
                          }
                        >
                          <TruncatedToolTipChildren>
                            {linkConfig && identifierValue ? (
                              linkConfig.isExternal ? (
                                <a
                                  href={`${linkConfig.url.replace(":identifier", identifierValue)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-text-link cursor-pointer"
                                >
                                  {cellContent}
                                </a>
                              ) : (
                                <Link
                                  to={`${linkConfig.url.replace(":identifier", identifierValue)}`}
                                  className="text-primary hover:underline text-text-link cursor-pointer"
                                >
                                  {cellContent}
                                </Link>
                              )
                            ) : cell.column.id === "added_ts" || cell.column.id === "updatedTs" ? (
                              <span>{moment(row.original[cell.column.id]).format("DD-MMM-YYYY h:mm A")}</span>
                            ) : (
                              cellContent
                            )}
                          </TruncatedToolTipChildren>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columnsConfig.length + 1} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!hidePagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(table.getFilteredRowModel().rows.length / perPage.value)}
          perPage={perPage}
          perPageOptions={paginationConfig.perPageOptions!}
          onPageChange={(page: any) => setCurrentPage(page)}
          onPerPageChange={(count: any) => {
            setPerPage(count);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  ) : null;
};

export default CommonTableView;
